import { Button, Modal } from '@birdi/components-common';
import { SubmitHandler, useForm } from 'react-hook-form';
import { css } from '@emotion/react';
import BriefSVG from '@birdi/icons/brief.svg';
import { BirdiTheme, next } from '@birdi/theme';
import { mapFolders as mapFoldersApi, stroot } from '@birdi/js-sdk';

interface CreateFolderInputs {
  name: string;
}

interface CreateFolderFormProps {
  onSuccess: () => void;
  selectedFolder: {id: string, name: string} | null;
  existingFolders: Array<{id: string, name: string}>;
}

const CreateFolderForm = ({ onSuccess, selectedFolder , existingFolders}: CreateFolderFormProps) => {

  const { register, handleSubmit, formState: { errors }, watch } = useForm<CreateFolderInputs>({
    defaultValues: {
      name: selectedFolder?.name || '',
    }
  });

  const folderName = watch('name');

  const onSubmit: SubmitHandler<CreateFolderInputs> = async (props) => {
    try {
      if (selectedFolder) {
        const renamedFolder = await mapFoldersApi.renameFolder(stroot('soov5f'), selectedFolder.id, { name: props.name });
        console.log('renamedFolder', renamedFolder);
      } else {
        const createdFolder = await mapFoldersApi.postFolder(stroot('soov5f'), { name: props.name });
        console.log('createdFolder', createdFolder);
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  }

  // Check if folder name already exists (excluding current folder when renaming)
  const isDuplicateName = existingFolders.some(folder => {
    const isCurrentFolder = selectedFolder && folder.id === selectedFolder.id;
    return !isCurrentFolder && folder.name.toLowerCase() === folderName?.toLowerCase();
  });

  return (
    <form id="create-folder" css={next.form.root} onSubmit={handleSubmit(onSubmit)}>
      <label css={next.form.field.root}>
        <span>Folder name</span>
        <input
          {...register('name', {
            required: 'Folder name cannot be empty.',
            setValueAs: (x: string) => x.trim(),
            validate: (value) => {
              if (!value) return true; // Let required validation handle empty
              if (isDuplicateName) {
                return 'A folder with this name already exists.';
              }
              return true;
            }
          })}
          css={next.inputs.text}
          aria-invalid={errors.name ? 'true' : 'false'}
        />
        {errors.name &&
          <p
            role="alert"
            css={(theme: BirdiTheme) => css`
              margin: 0;
              color: red;
            `}
          >
            {errors.name.message}
          </p>
        }
      </label>
    </form>
  );
}

interface CreateFolderModalProps {
  open: boolean;
  onChange: (value: boolean) => void;
  onFolderCreated?: () => void;
  selectedFolder?: {id: string, name: string} | null;
  existingFolders?: Array<{id: string, name: string}>;
}

export const CreateFolderModal = ({ open, onChange, onFolderCreated, selectedFolder = null, existingFolders = [] }: CreateFolderModalProps) => {
  return (
    <Modal.Root open={open} onOpenChange={onChange}>
      <Modal.Header>
        {selectedFolder ? 'Rename Folder' : 'Create Folder'}
      </Modal.Header>

      <Modal.Body>
        <CreateFolderForm
          onSuccess={() => {
            onFolderCreated?.();
            onChange(false);
          }}
          selectedFolder={selectedFolder}
          existingFolders={existingFolders}
        />
      </Modal.Body>

      <Modal.Footer buttons>
        <Button variant='outline' onClick={() => onChange(false)}>Cancel</Button>
        <Button variant='solid' type="submit" form="create-folder">{selectedFolder ? 'Update' : 'Create'}</Button>
      </Modal.Footer>
    </Modal.Root>
  );
};
